import { Box, Grid as Grid } from "@mui/material";
import React, { Dispatch, SetStateAction, useRef } from "react";
import { isEmpty } from "lodash";

import AddressFormInputUseForm from "../../../components/formComponents/AddressFormInputUseForm";
import { FormInputText } from "../../../components/formComponents/FormInputText";
import {
  OrderingPhysicianDetails,
  PrimaryPhysicianDetails,
} from "../../../models/PhysicianDetails";
import { FormInputBox, SwitchGrid, Body } from "../styles/OrderingPhysician";
import { rules } from "../../../utils/validations/Validations";
import { PhoneFaxExtInput } from "../../../components/formComponents/PhoneFaxExtInput";
import {
  physicianFormProps,
  GetNPPESDataActionDispatchTypes,
} from "../../../constants/PhysicianDetails";
import NppesSearchComponent from "../../../components/custom/nppesSearchComponent/container/NppesSearchContainer";

export interface PropsFromState {
  control?: any;
  getValues?: any;
  setValue?: any;
  reset?: any;
  choosePhysician: (
    option: PrimaryPhysicianDetails | OrderingPhysicianDetails
  ) => void;
  physician: PrimaryPhysicianDetails | OrderingPhysicianDetails | null;
  names: any;
  physicianFormName: string;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setAddManually?: (value: boolean) => void;
  setOpenAutocomplete: Dispatch<SetStateAction<boolean>>;
  openAutocomplete: boolean;
  isReadOnly?: boolean;
  noValidate?: boolean;
}
export interface PropsFromDispatch {
  resetStates: (actionType: string[]) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const PhysicianInputForm: React.FC<AllProps> = ({
  choosePhysician,
  control,
  getValues,
  names,
  open,
  reset,
  setOpen,
  setValue,
  physicianFormName,
  setAddManually,
  resetStates,
  openAutocomplete,
  setOpenAutocomplete,
  isReadOnly,
  noValidate,
}: AllProps) => {
  const handleSelect = (option: any) => {
    choosePhysician(option);
    if (!isEmpty(physicianFormName)) {
      setValue(physicianFormName, option.physicianName || null);
    }
    setAddManually && setAddManually(false);
    resetStates &&
      resetStates([GetNPPESDataActionDispatchTypes.GET_NPPES_DATA_RESET]);
    setOpen && setOpen(false);
  };

  return (
    <>
      <Grid container>
        <Grid
          size={{ xs: 12 }}
          data-testid="patient-header-primary-physician"
          sx={SwitchGrid(isReadOnly ? isReadOnly : false)}
        >
          <NppesSearchComponent
            NppesOpen={open}
            NppesSetOpen={setOpen}
            handleSelect={handleSelect}
            openAutocomplete={openAutocomplete}
            setOpenAutocomplete={setOpenAutocomplete}
            nppesSearchInjectedto={physicianFormName}
          />
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={[Body, SwitchGrid(isReadOnly ? isReadOnly : false)]}
        >
          <PhysicianFormInputs
            control={control}
            reset={reset}
            getValues={getValues}
            setValue={setValue}
            names={names}
            physicianFormName={physicianFormName}
            noValidate={noValidate}
          />
        </Grid>
      </Grid>
    </>
  );
};

type PhysicianFormInputsProps = {
  control: any;
  reset: any;
  getValues: any;
  setValue: any;
  names: any;
  isHeader?: boolean;
  physicianFormName: string;
  noValidate?: boolean;
};

export const PhysicianFormInputs = (props: PhysicianFormInputsProps) => {
  const {
    control,
    reset,
    getValues,
    setValue,
    names,
    isHeader,
    physicianFormName,
    noValidate,
  } = props;

  const isValidationDisabled = !!noValidate;
  const pickRule = <T,>(rule: T) => (isValidationDisabled ? false : rule);
  let phoneRule;
  if (isValidationDisabled) {
    phoneRule = false;
  } else if (isHeader) {
    phoneRule = rules.mobilePhone;
  } else {
    phoneRule = rules.phone;
  }

  const searchInput = useRef(null);
  const labels = [
    { label: "Street 1: ", name: names.address[0], helper: pickRule(rules.streetName1), textLength: 100 },
    { label: "Street 2: ", name: names.address[1], helper: pickRule(rules.streetName2), textLength: 100 },
    { label: "City: ", name: names.address[2], helper: pickRule(rules.cityRequired), textLength: 100 },
    { label: "State: ", name: names.address[4], helper: pickRule(rules.stateRequired), textLength: 100 },
    { label: "County: ", name: names.address[3], textLength: 100 },
    {
      label: "Zip Code: ",
      name: names.address[5],
      helper: pickRule(rules.physicianZipcode) || { pattern: rules.zipcode.pattern },
      textLength: 10,
    },
    { name: names.address[6] },
    { name: names.address[7] },
  ];

  const formProps = physicianFormProps(physicianFormName);
  return (
    <>
      <Grid size={{ xs: 12 }} pt={isHeader ? "0.5rem" : "1.5rem"}>
        <Grid container>
          <Grid size={{ xs: 3 }}>
            <Box sx={FormInputBox}>
              <FormInputText
                name={names.physicianName}
                control={control}
                label={formProps.physicianName}
                fieldrequired={"true"}
                helper={noValidate ? false : formProps.rule}
                placeholder="Add here..."
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Box sx={FormInputBox}>
              <FormInputText
                name={names.physicianNpiId}
                control={control}
                label={formProps.physicianNpi}
                textLength={10}
                fieldrequired={"true"}
                helper={noValidate ? false : rules.physicianNpi}
                placeholder="Add here..."
                inputType="onlyNumber"
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Box sx={FormInputBox}>
              <PhoneFaxExtInput
                label="Phone:"
                control={control}
                phoneFaxName={names.phoneNo}
                extName={names.phoneExt}
                rules={phoneRule}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Box sx={FormInputBox}>
              <PhoneFaxExtInput
                label="Fax:"
                control={control}
                phoneFaxName={names.fax}
                rules={rules.fax}
                isFax
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid container>
        <Grid size={{ xs: 12 }}>
          <AddressFormInputUseForm
            control={control}
            searchInput={searchInput}
            reset={reset}
            getValues={getValues}
            setValue={setValue}
            labels={labels}
            addressVariant="subtitle1"
          />
        </Grid>
      </Grid>
    </>
  );
};

export default PhysicianInputForm;
